import { memo, useMemo, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';
import type { SingleValue } from 'react-select';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Media } from '../../../../../theme/Media.tsx';
import { Select } from '../../../../Form/Select/index.tsx';
import { dataTableSection } from '../../constants.ts';
import { useTableContext } from '../../context.tsx';
import type { Option } from '../../types.ts';
import { Info } from '../Info/index.tsx';
import { PageInfo } from '../PageInfo/index.tsx';
import { PaginationControls } from '../PaginationControls/index.tsx';
import { paginationPresetDefault, paginationPresetReduced } from './constants.ts';
import { PageInfoWrapper, PaginationWrapper } from './styled.ts';
import type { PaginationProps } from './types.ts';

export const Pagination: FC<PaginationProps> = memo(function Pagination({ className, info, pagination, isLoading, dataTestId }) {
  const { compact } = useTableContext();
  const isMobile = useMediaQuery({ query: Media.xsMax });
  const selectedPerPage = useMemo(
    (): Option<number> => ({ label: String(pagination.perPage), value: pagination.perPage }),
    [pagination.perPage],
  );
  const isReducedPaginationPreset = useMemo(
    () => !!paginationPresetReduced.find((option) => option.value === pagination.perPage),
    [pagination.perPage],
  );

  const handleSetPerPage = useCallbackRef((option: SingleValue<Option<number>>) => {
    if (option) {
      pagination.setPerPage(option.value);
      pagination.setPage(0);
    }
  });

  const nextPage = useCallbackRef(() => {
    pagination.setPage((prev) => prev + 1);
  });

  const prevPage = useCallbackRef(() => {
    pagination.setPage((prev) => prev - 1);
  });

  if (!isLoading && info.total <= pagination.perPage) {
    return <Info total={info.total} isLoading={isLoading} />;
  }

  return (
    <PaginationWrapper className={className} $compact={compact} {...dataTableSection} data-testid={dataTestId}>
      {!isMobile && (
        <div data-testid='per-page-dropdown'>
          <Select
            options={isReducedPaginationPreset ? paginationPresetReduced : paginationPresetDefault}
            value={selectedPerPage}
            onChange={handleSetPerPage}
            menuPlacement='auto'
            data-testid='select-op'
          />
        </div>
      )}
      <PageInfoWrapper>
        <PageInfo dataTestId='from-to-total-pagination' {...info} isLoading={isLoading} />
      </PageInfoWrapper>
      <PaginationControls page={pagination.page} perPage={pagination.perPage} total={info.total} onPrev={prevPage} onNext={nextPage} />
    </PaginationWrapper>
  );
});

export { usePageInfo } from './usePageInfo.ts';
export { usePagination } from './usePagination.ts';
